import { useState, useEffect, Component, type ReactNode } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from './hooks/useAuth'
import { supabase } from './services/supabase'
import { disconnectSocket } from './services/socket'
import { useAuthStore } from './store/authStore'
import Login from './pages/Login'
import Register from './pages/Register'
import Game from './pages/Game'
import Privacy from './pages/Privacy'
import Terms from './pages/Terms'

// Error boundary to catch render crashes
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }
  static getDerivedStateFromError() { return { hasError: true } }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-gray-950 via-blue-950 to-gray-950 p-4">
          <p className="text-red-400 text-lg font-mono mb-4">Something went wrong</p>
          <button
            onClick={() => {
              disconnectSocket()
              useAuthStore.getState().logout()
              supabase.auth.signOut().catch(() => {})
              Object.keys(localStorage).filter(k => k.startsWith('sb-')).forEach(k => localStorage.removeItem(k))
              window.location.href = '/login'
            }}
            className="px-4 py-2 text-sm text-slate-400 hover:text-white border border-slate-700 hover:border-slate-500 rounded-lg transition-colors"
          >
            Reset &amp; Return to Login
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

const Loading = () => {
  const [showReset, setShowReset] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setShowReset(true), 3000)
    return () => clearTimeout(timer)
  }, [])

  const clearSession = async () => {
    disconnectSocket()
    useAuthStore.getState().logout()
    try { await supabase.auth.signOut() } catch { /* ignore */ }
    Object.keys(localStorage)
      .filter((k) => k.startsWith('sb-'))
      .forEach((k) => localStorage.removeItem(k))
    window.location.href = '/login'
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-gray-950 via-blue-950 to-gray-950">
      <div className="flex flex-col items-center gap-6">
        <div className="relative">
          <Loader2 className="w-16 h-16 text-cyan-400 animate-spin" />
          <div className="absolute inset-0 w-16 h-16 bg-cyan-400/20 rounded-full blur-xl animate-pulse" />
        </div>
        <div className="text-center">
          <p className="text-cyan-300 text-lg font-mono tracking-wider">{/* // */}Initializing System...</p>
          <div className="flex gap-1 justify-center mt-2">
            <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
            <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" style={{ animationDelay: '200ms' }} />
            <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" style={{ animationDelay: '400ms' }} />
          </div>
        </div>
      </div>
      {showReset && (
        <button
          onClick={clearSession}
          className="mt-8 px-4 py-2 text-sm text-slate-400 hover:text-white border border-slate-700 hover:border-slate-500 rounded-lg transition-colors"
        >
          Session stuck? Reset
        </button>
      )}
    </div>
  )
}

function App() {
  const { hasSession, hasProfile, isLoading } = useAuth()

  if (isLoading) {
    return <Loading />
  }

  return (
    <Router>
      <Routes>
        <Route
          path="/login"
          element={
            hasSession ? (
              hasProfile ? <Navigate to="/" /> : <Navigate to="/register" />
            ) : (
              <Login />
            )
          }
        />
        <Route
          path="/register"
          element={hasProfile ? <Navigate to="/" /> : <Register />}
        />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route
          path="/"
          element={
            hasSession ? (
              hasProfile ? <Game /> : <Navigate to="/register" />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
      </Routes>
    </Router>
  )
}

export default function WrappedApp() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  )
}
